import { User } from '../types';
import { apiClient } from './api';

export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

interface GoogleAuthRequest {
  id_token: string;
}

interface ConfirmEmailRequest {
  token: string;
}

interface LoginResponse {
  token: string;
  user: User;
}

class AuthService {
  async register(data: RegisterRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/register', {
      user: data,
    });

    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', data);

    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  }

  async loginWithGoogle(data: GoogleAuthRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/google', data);

    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  async forgotPassword(
    data: ForgotPasswordRequest
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/auth/forgot-password',
      data
    );
    return response.data;
  }

  async resetPassword(
    data: ResetPasswordRequest
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/auth/reset-password',
      data
    );
    return response.data;
  }

  async confirmEmail(data: ConfirmEmailRequest): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(
      '/auth/confirm-email',
      data
    );
    return response.data;
  }

  async validateToken(): Promise<{ valid: boolean; user?: User }> {
    try {
      const response = await apiClient.get<{ valid: boolean; user: User }>(
        '/auth/validate'
      );
      return response.data;
    } catch (error) {
      return { valid: false };
    }
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('user');
      }
    }
    return null;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export const authService = new AuthService();
